import React, { useState } from 'react'
import { supabase } from './supabaseClient'
import { C } from './theme'
import { parseSageVendas } from './utils/parseSageVendas'
import { FileText, CheckCircle, AlertTriangle, Upload, Loader } from 'lucide-react'

// Importar o "Mapa de vendas" do Sage.
//
// Nunca grava directamente. Lê, mostra mês a mês com o confronto contra o que a
// plataforma já sabe, e só grava depois de confirmares. O risco de ler PDFs
// nunca esteve na leitura — está em aceitar em silêncio.

const fmt = (n) => Number(n || 0).toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const MESES = ['', 'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']

const MESES_CURTOS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']

export default function ImportarSage({ ano, jaImportado, plataformaPorMes, declarada = [], onImportado }) {
  const [lendo, setLendo] = useState(false)
  const [res, setRes] = useState(null)
  const [erro, setErro] = useState('')
  const [gravando, setGravando] = useState(false)

  const ler = async (file) => {
    if (!file) return
    setLendo(true); setErro(''); setRes(null)
    try {
      const r = await parseSageVendas(file)
      if (!r.ok) {
        // Juntar a amostra do texto extraído: sem ela fica-se sem saber se o
        // problema é o ficheiro ou o leitor.
        setErro(r.erro + (r.amostra ? '\n\nFoi isto que consegui ler do PDF:\n' + r.amostra : ''))
        return
      }
      setRes(r)
    } catch (e) {
      setErro('Não consegui ler o PDF: ' + (e.message || e))
    } finally { setLendo(false) }
  }

  const gravar = async () => {
    if (!res) return
    const doAno = res.meses.filter(m => m.ano === Number(ano))
    if (doAno.length === 0) { setErro(`O relatório não tem meses de ${ano}.`); return }
    setGravando(true); setErro('')
    try {
      const { data: { user } } = await supabase.auth.getUser()
      const linhas = doAno.map(m => ({
        ano: m.ano, mes: m.mes,
        valor_sem_iva: m.semIva,
        valor_com_iva: m.comIva,
        n_documentos: m.n,
        origem: 'Sage — Mapa de vendas',
        periodo_ini: res.periodoIni ? res.periodoIni.split('/').reverse().join('-') : null,
        periodo_fim: res.periodoFim ? res.periodoFim.split('/').reverse().join('-') : null,
        importado_por: user?.id || null,
        importado_em: new Date().toISOString(),
      }))
      const { error } = await supabase.from('faturacao_declarada')
        .upsert(linhas, { onConflict: 'ano,mes' })
      if (error) throw new Error(error.message)
      setRes(null)
      await onImportado?.()
    } catch (e) {
      setErro('Erro ao gravar: ' + (e.message || e))
    } finally { setGravando(false) }
  }

  const doAno = res ? res.meses.filter(m => m.ano === Number(ano)) : []
  const somaAno = doAno.reduce((s, m) => s + m.semIva, 0)

  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '6px', marginBottom: '14px', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', padding: '12px 16px', background: C.bg, borderBottom: `1px solid ${C.border}` }}>
        <span style={{ fontSize: '13px', fontWeight: '800', color: C.text, display: 'flex', alignItems: 'center', gap: '7px' }}>
          <FileText size={15} color={C.primary} />
          Facturação real ({ano})
          {jaImportado > 0 && (
            <span style={{ fontSize: '11px', fontWeight: '600', color: C.success }}>
              · {jaImportado} mês(es) importado(s) do Sage
            </span>
          )}
        </span>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '7px 14px', border: 'none', borderRadius: '4px', background: C.primary, color: '#fff', cursor: lendo ? 'wait' : 'pointer', fontSize: '12px', fontWeight: '700' }}>
          {lendo ? <><Loader size={13} /> A ler…</> : <><Upload size={13} /> Importar Mapa de vendas (PDF)</>}
          <input type="file" accept="application/pdf" style={{ display: 'none' }} disabled={lendo}
            onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; ler(f) }} />
        </label>
      </div>

      <div style={{ padding: '10px 16px', fontSize: '11.5px', color: C.textMuted, lineHeight: 1.6, borderBottom: res || erro ? `1px solid ${C.border}` : 'none' }}>
        O Sage tem tudo o que foi facturado — a plataforma só conhece o que passou por ela.
        Nos meses importados manda o Sage; a camada <strong>Contratado, por facturar</strong> continua a vir da plataforma,
        porque ainda não foi facturada e por isso não pode estar no relatório.
        {/* ATE ONDE E QUE ISTO JA SABE.
            Sem esta linha nao havia forma de saber se o ultimo relatorio
            importado era de ontem ou de ha tres meses — e um mes por importar
            faz o ecra cair para o que a plataforma sabe, sem dizer nada. */}
        {(() => {
          const doAno = (declarada || []).filter(d => Number(d.ano) === Number(ano) && d.importado_em)
          if (doAno.length === 0) return null
          const ultimo = doAno.reduce((a, b) => (a.importado_em > b.importado_em ? a : b))
          const dt = (s) => new Date(s).toLocaleDateString('pt-PT')
          return (
            <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: `1px solid ${C.border}`, color: C.text }}>
              <strong>Último relatório importado:</strong>{' '}
              {ultimo.periodo_ini && ultimo.periodo_fim
                ? <>período <strong>{dt(ultimo.periodo_ini)} a {dt(ultimo.periodo_fim)}</strong></>
                : <>{doAno.length} mês(es) de {ano}</>}
              {' · '}carregado a {new Date(ultimo.importado_em).toLocaleDateString('pt-PT')}
              {' às '}{new Date(ultimo.importado_em).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}
              <div style={{ color: C.textMuted, marginTop: '2px' }}>
                Meses cobertos: {doAno.map(d => MESES_CURTOS[Number(d.mes) - 1]).join(', ')}.
                O que vier depois desta data ainda não está aqui.
              </div>
            </div>
          )
        })()}
      </div>

      {erro && (
        <div style={{ padding: '10px 16px', background: C.dangerSoft, color: C.danger, fontSize: '12px', fontWeight: '600', whiteSpace: 'pre-wrap', fontFamily: erro.includes('consegui ler') ? 'monospace' : 'inherit' }}>{erro}</div>
      )}

      {res && (
        <div style={{ padding: '14px 16px' }}>
          {/* A verificação: sem isto não se importa nada */}
          <div style={{ padding: '10px 12px', borderRadius: '4px', marginBottom: '12px', fontSize: '12px', fontWeight: '600',
            background: res.conferido ? C.successSoft : '#fff4ce',
            color: res.conferido ? C.success : '#92400e',
            border: `1px solid ${res.conferido ? C.success : '#c07400'}55` }}>
            {res.conferido ? (
              <><CheckCircle size={13} style={{ verticalAlign: 'middle', marginRight: 5 }} />
                <strong>Leitura verificada.</strong> As {res.docs.length} linhas somam {fmt(res.somaSemIva)} € s/IVA,
                exactamente o total impresso no relatório.</>
            ) : (
              <><AlertTriangle size={13} style={{ verticalAlign: 'middle', marginRight: 5 }} />
                <strong>A leitura não fecha com o total do relatório.</strong> Não importes até se perceber porquê —
                pode faltar uma página ou haver linhas com formato diferente.</>
            )}
            {res.avisos.map((a, i) => (
              <div key={i} style={{ marginTop: 4, fontWeight: '400' }}>{a}</div>
            ))}
          </div>

          {/* A acção fica JUNTO da verificação: estava no fim da tabela, fora do
              ecrã, e o user ficou com a pré-visualização aberta a pensar que já
              tinha importado. Uma acção que não se vê é uma acção que não existe. */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
            <button onClick={gravar} disabled={gravando || doAno.length === 0}
              style={{ padding: '9px 20px', border: 'none', borderRadius: '4px', background: res.conferido ? C.primary : C.warning, color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '800' }}>
              {gravando ? 'A gravar…' : `✓ Confirmar e importar ${doAno.length} mês(es)`}
            </button>
            <button onClick={() => setRes(null)} disabled={gravando}
              style={{ padding: '8px 14px', border: `1px solid ${C.border}`, borderRadius: '4px', background: C.surface, color: C.textMuted, cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>
              Cancelar
            </button>
            <span style={{ fontSize: '11.5px', color: C.textMuted }}>
              Nada é gravado até confirmares. Confere os meses abaixo primeiro.
            </span>
          </div>

          {res.periodoIni && (
            <p style={{ margin: '0 0 8px', fontSize: '11.5px', color: C.textMuted }}>
              Período do relatório: <strong style={{ color: C.text }}>{res.periodoIni} a {res.periodoFim}</strong> · {res.docs.length} documento(s)
            </p>
          )}

          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' }}>
            <thead>
              <tr style={{ background: C.surfaceAlt }}>
                <th style={th}>Mês</th>
                <th style={{ ...th, textAlign: 'right' }}>Facturas</th>
                <th style={{ ...th, textAlign: 'right' }}>Sage s/IVA</th>
                <th style={{ ...th, textAlign: 'right' }}>A plataforma sabia</th>
                <th style={{ ...th, textAlign: 'right' }}>Diferença</th>
              </tr>
            </thead>
            <tbody>
              {doAno.map(m => {
                const plat = Number(plataformaPorMes?.[m.mes] || 0)
                const dif = m.semIva - plat
                return (
                  <tr key={m.mes} style={{ borderTop: `1px solid ${C.border}` }}>
                    <td style={{ padding: '6px 12px', fontWeight: '700', color: C.text }}>{MESES[m.mes]}</td>
                    <td style={{ padding: '6px 12px', textAlign: 'right', color: C.textMuted }}>{m.n}</td>
                    <td style={{ padding: '6px 12px', textAlign: 'right', fontWeight: '700', color: C.text }}>{fmt(m.semIva)} €</td>
                    <td style={{ padding: '6px 12px', textAlign: 'right', color: C.textMuted }}>{plat > 0 ? fmt(plat) + ' €' : '—'}</td>
                    <td style={{ padding: '6px 12px', textAlign: 'right', fontWeight: '700', color: dif > 0.5 ? C.warning : C.textMuted }}>
                      {dif > 0.5 ? '+' + fmt(dif) + ' €' : '—'}
                    </td>
                  </tr>
                )
              })}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: `2px solid ${C.border}`, background: C.surfaceAlt }}>
                <td colSpan="2" style={{ padding: '8px 12px', fontWeight: '800', color: C.text }}>Total {ano}</td>
                <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: '800', color: C.primary, fontSize: '13px' }}>{fmt(somaAno)} €</td>
                <td colSpan="2"></td>
              </tr>
            </tfoot>
          </table>

          <p style={{ margin: '10px 0 0', fontSize: '11px', color: C.textMuted, lineHeight: 1.6 }}>
            A coluna <strong>Diferença</strong> é facturação que existe e a plataforma não conhecia —
            facturas emitidas fora do site. É por isso que o Sage manda.
          </p>

        </div>
      )}
    </div>
  )
}

const th = { padding: '7px 12px', textAlign: 'left', fontWeight: '700', color: C.textMuted, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.4px' }
